/**
 * Transfer Items Table Component
 * Grouped expandable view with item search and lot/batch search.
 * For products: groups by product+variant, shows per-lot pallet/case/unit inputs.
 * For chemicals: groups by item, shows single quantity input.
 */

import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  Typography, Box, IconButton, Collapse, InputAdornment, Button, useTheme, alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon
} from '@mui/icons-material';

export default function TransferItemsTable({
  availableLots,
  items,
  selectedItems,
  onQuantityChange,
  isChemical,
  isProduct
}) {
  const theme = useTheme();
  const [expandedItems, setExpandedItems] = useState({});
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [lotBatchSearchTerms, setLotBatchSearchTerms] = useState({});
  const [lotModes, setLotModes] = useState({}); // { [lotId]: 'container' | 'measure' }

  // Build grouped data for products (key = 'productId_variantId')
  // or for chemicals/caps/etc (key = itemId)
  const groupedData = useMemo(() => {
    return Object.keys(availableLots).map(key => {
      const lots = availableLots[key] || [];

      if (isProduct) {
        const firstLot    = lots[0];
        const itemName    = firstLot?.products?.name || 'Unknown';
        const variantName = firstLot?.variant_name   || null;
        const totalBaseUnits = lots.reduce((sum, l) => sum + (l.total_base_units || 0), 0);

        return {
          key,
          itemName,
          variantName,
          displayName: variantName ? `${itemName} — ${variantName}` : itemName,
          lots,
          totalAvailable: totalBaseUnits,
          isProduct: true
        };
      } else {
        // Chemicals / caps / bottles — key = itemId
        const item           = items.find(i => i.id === key);
        const totalAvailable = lots.reduce((sum, l) => sum + (l.current_quantity || 0), 0);
        const totalUnitQty   = isChemical ? lots.reduce((sum, l) => sum + (l.current_unit_quantity  || 0), 0) : 0;
        const totalBaseQty   = isChemical ? lots.reduce((sum, l) => sum + (l.current_total_quantity || 0), 0) : 0;
        const unitType       = isChemical ? (lots[0]?.raw_materials?.primary_unit_type    || 'units') : null;
        const unitMeasure    = isChemical ? (lots[0]?.raw_materials?.primary_unit_measure || '')      : null;

        return {
          key,
          itemName: item?.name || 'Unknown',
          variantName: null,
          displayName: item?.name || 'Unknown',
          lots,
          totalAvailable,
          totalUnitQty,
          totalBaseQty,
          unitType,
          unitMeasure,
          isProduct: false
        };
      }
    }).sort((a, b) => {
      // Sort by product name first, then by variant size descending (16oz > 8oz > 4oz)
      const nameA = a.itemName.toLowerCase();
      const nameB = b.itemName.toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const extractSize = (s) => { const m = (s || '').match(/(\d+(\.\d+)?)/); return m ? parseFloat(m[1]) : 0; };
      return extractSize(b.variantName) - extractSize(a.variantName); // highest first
    });
  }, [availableLots, items, isChemical, isProduct]);

  const filteredData = useMemo(() => {
    if (!itemSearchTerm) return groupedData;
    const searchLower = itemSearchTerm.toLowerCase();
    return groupedData.filter(d => d.displayName.toLowerCase().includes(searchLower));
  }, [groupedData, itemSearchTerm]);

  const toggleExpand = (key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getFilteredLots = (key, lots) => {
    const searchTerm = (lotBatchSearchTerms[key] || '').toLowerCase();
    if (!searchTerm) return lots;
    return lots.filter(lot => {
      const lotNumber   = (lot.lot_number   || '').toLowerCase();
      const batchNumber = (lot.batch_number || '').toLowerCase();
      return lotNumber.includes(searchTerm) || batchNumber.includes(searchTerm);
    });
  };

  // Calculate base units from pallet/case/unit selection for a product lot
  const calcProductBaseUnits = (selected, lot) => {
    if (!selected) return 0;
    const pallets        = selected.pallets || 0;
    const cases          = selected.cases   || 0;
    const units          = selected.units   || 0;
    const unitsPerCase   = lot.units_per_case   || 1;
    const casesPerPallet = lot.cases_per_pallet || 1;
    return (pallets * casesPerPallet * unitsPerCase) + (cases * unitsPerCase) + units;
  };

  // Check if a product lot row has any transfer qty entered
  const hasProductTransfer = (selected) => {
    if (!selected) return false;
    return (selected.pallets || 0) + (selected.cases || 0) + (selected.units || 0) > 0;
  };

  return (
    <Box>
      {/* Item Search */}
      <TextField
        fullWidth
        placeholder="Search items..."
        value={itemSearchTerm}
        onChange={(e) => setItemSearchTerm(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: theme.palette.primary.main }} />
            </InputAdornment>
          )
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'white'
          }
        }}
      />

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Item</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>
                {isProduct ? 'Total Available (units)' : 'Total Available'}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map(({ key, displayName, lots, totalAvailable, totalUnitQty, totalBaseQty, unitType, unitMeasure }) => {
              const isExpanded   = expandedItems[key];
              const filteredLots = getFilteredLots(key, lots);

              return (
                <React.Fragment key={key}>
                  {/* Main Item Row */}
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'inherit'
                    }}
                    onClick={() => toggleExpand(key)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {displayName}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {isChemical
                        ? `${totalUnitQty.toLocaleString()} ${unitType} (${totalBaseQty.toLocaleString()} ${unitMeasure})`
                        : totalAvailable.toLocaleString()
                      }
                    </TableCell>
                  </TableRow>

                  {/* Expanded Lot/Batch Details */}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                          p: 3,
                          bgcolor: alpha(theme.palette.grey[50], 0.5),
                          borderTop:    `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                        }}>
                          {/* Lot/Batch Search */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                              Lot/Batch Details ({filteredLots.length}{lotBatchSearchTerms[key] ? ` of ${lots.length}` : ''})
                            </Typography>
                            <TextField
                              placeholder="Search lot/batch..."
                              size="small"
                              value={lotBatchSearchTerms[key] || ''}
                              onChange={(e) => setLotBatchSearchTerms(prev => ({ ...prev, [key]: e.target.value }))}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                                  </InputAdornment>
                                )
                              }}
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  bgcolor: 'white',
                                  fontSize: '0.85rem'
                                }
                              }}
                            />
                          </Box>

                          {/* ---- Product lot table ---- */}
                          {isProduct ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Lot Number</TableCell>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Batch Number</TableCell>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Available</TableCell>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Transfer Qty</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredLots.map(lot => {
                                  const selected    = selectedItems[key]?.[lot.id];
                                  const baseUnits   = calcProductBaseUnits(selected, lot);
                                  const hasTransfer = hasProductTransfer(selected);

                                  const upc         = lot.units_per_case   || 1;
                                  const cpp         = lot.cases_per_pallet || 1;
                                  const totalBudget = lot.total_base_units || 0;
                                  const entPallets  = selected?.pallets || 0;
                                  const entCases    = selected?.cases   || 0;
                                  const entUnits    = selected?.units   || 0;

                                  // Available display: only show non-zero tiers
                                  const palletsAvail = lot.enable_pallets && (lot.pallet_quantity || 0) > 0;
                                  const casesAvail   = lot.enable_cases   && (lot.case_quantity   || 0) > 0;
                                  const unitsAvail   = (lot.unit_quantity || 0) > 0;

                                  // Input display + dynamic max (each tier's max shrinks as others are filled)
                                  const showCasesInput = lot.enable_cases && (casesAvail || palletsAvail);
                                  const effPalletsMax = Math.max(0, Math.min(
                                    lot.pallet_quantity || 0,
                                    Math.floor(Math.max(0, totalBudget - entCases * upc - entUnits) / (cpp * upc))
                                  ));
                                  const effCasesMax = Math.max(0, Math.min(
                                    (lot.case_quantity || 0) + (lot.pallet_quantity || 0) * cpp,
                                    Math.floor(Math.max(0, totalBudget - entPallets * cpp * upc - entUnits) / upc)
                                  ));
                                  const effUnitsMax = Math.max(0, totalBudget - entPallets * cpp * upc - entCases * upc);

                                  return (
                                    <TableRow key={lot.id}>
                                      <TableCell>{lot.lot_number   || '-'}</TableCell>
                                      <TableCell>{lot.batch_number || '-'}</TableCell>

                                      {/* Available breakdown — only non-zero tiers */}
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {[
                                            palletsAvail && `${lot.pallet_quantity} ${lot.pallet_quantity === 1 ? 'Pallet' : 'Pallets'}`,
                                            casesAvail   && `${lot.case_quantity} ${lot.case_quantity === 1 ? 'Case' : 'Cases'}`,
                                            unitsAvail   && `${lot.unit_quantity} ${lot.unit_quantity === 1 ? 'Unit' : 'Units'}`
                                          ].filter(Boolean).join(' · ')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {(lot.total_base_units || 0).toLocaleString()} units total
                                        </Typography>
                                      </TableCell>

                                      {/* Transfer inputs — pallet/case/unit with budget-aware maxes */}
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                          {palletsAvail && (
                                            <TextField
                                              label={`Pallets / ${effPalletsMax}`}
                                              type="number"
                                              size="small"
                                              value={selected?.pallets || ''}
                                              onChange={(e) => onQuantityChange(key, lot.id, e.target.value, 'pallets')}
                                              inputProps={{ min: 0, max: effPalletsMax }}
                                              sx={{ width: 110, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                            />
                                          )}
                                          {showCasesInput && (
                                            <TextField
                                              label={`Cases / ${effCasesMax}`}
                                              type="number"
                                              size="small"
                                              value={selected?.cases || ''}
                                              onChange={(e) => onQuantityChange(key, lot.id, e.target.value, 'cases')}
                                              inputProps={{ min: 0, max: effCasesMax }}
                                              sx={{ width: 110, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                            />
                                          )}
                                          <TextField
                                            label={`Units / ${effUnitsMax}`}
                                            type="number"
                                            size="small"
                                            value={selected?.units || ''}
                                            onChange={(e) => onQuantityChange(key, lot.id, e.target.value, 'units')}
                                            inputProps={{ min: 0, max: effUnitsMax }}
                                            sx={{ width: 110, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                          />
                                          {hasTransfer && (
                                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                              = {baseUnits.toLocaleString()} units
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {filteredLots.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                                      <Typography variant="body2">
                                        {lotBatchSearchTerms[key] ? 'No matching lots found' : 'No lots available'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>

                          ) : (
                            /* ---- Chemical / caps / etc lot table ---- */
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Lot Number</TableCell>
                                  <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Batch Number</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Available</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Transfer Qty</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredLots.map(lot => {
                                  const lotUnitType    = lot.raw_materials?.primary_unit_type    || 'units';
                                  const lotUnitMeasure = lot.raw_materials?.primary_unit_measure || '';
                                  const lotUnitSize    = parseFloat(lot.raw_materials?.primary_unit_quantity) || 1;
                                  const lotMode        = isChemical && lotUnitSize > 0 ? (lotModes[lot.id] || 'container') : 'container';
                                  const enteredQty     = selectedItems[key]?.[lot.id]?.quantity || 0;

                                  const computedDisplay = isChemical && enteredQty > 0
                                    ? (lotMode === 'measure'
                                        ? `≈ ${parseFloat((enteredQty / lotUnitSize).toFixed(3))} ${lotUnitType}`
                                        : `= ${parseFloat((enteredQty * lotUnitSize).toFixed(3))} ${lotUnitMeasure}`)
                                    : null;

                                  const handleModeSwitch = (newMode) => {
                                    setLotModes(prev => ({ ...prev, [lot.id]: newMode }));
                                    onQuantityChange(key, lot.id, '', 'quantity', newMode);
                                  };

                                  return (
                                    <TableRow key={lot.id}>
                                      <TableCell>{lot.lot_number   || '-'}</TableCell>
                                      <TableCell>{lot.batch_number || '-'}</TableCell>
                                      <TableCell align="right">
                                        {isChemical
                                          ? `${lot.current_unit_quantity || 0} ${lotUnitType} (${lot.current_total_quantity || 0} ${lotUnitMeasure})`
                                          : (lot.current_quantity || 0).toLocaleString()
                                        }
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                          {isChemical && lotUnitSize > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                                              <Button
                                                size="small"
                                                variant={lotMode === 'container' ? 'contained' : 'outlined'}
                                                onClick={() => handleModeSwitch('container')}
                                                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0, textTransform: 'none' }}
                                              >
                                                {lotUnitType}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant={lotMode === 'measure' ? 'contained' : 'outlined'}
                                                onClick={() => handleModeSwitch('measure')}
                                                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0, textTransform: 'none' }}
                                              >
                                                {lotUnitMeasure}
                                              </Button>
                                            </Box>
                                          )}
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={selectedItems[key]?.[lot.id]?.quantity || ''}
                                            onChange={(e) => onQuantityChange(key, lot.id, e.target.value, 'quantity', lotMode)}
                                            inputProps={{ min: 0, max: lotMode === 'measure' ? (lot.current_total_quantity || 0) : (lot.current_unit_quantity || 0) }}
                                            sx={{ width: 110, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input[type=number]': { MozAppearance: 'textfield' } }}
                                          />
                                          {computedDisplay && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              {computedDisplay}
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {filteredLots.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                                      <Typography variant="body2">
                                        {lotBatchSearchTerms[key] ? 'No matching lots found' : 'No lots available'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {itemSearchTerm ? 'No items match your search' : 'No items available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
